import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Medal } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import useAdminLoad from "@/hooks/useAdminLoad";
import { Empty, LoadState, Panel, Pill } from "@/components/admin/AdminUI";
import ConfirmDialog from "@/components/admin/ConfirmDialog";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/format";

export default function FoundingTab() {
  const { data, error, loading, reload } = useAdminLoad("/admin/founding");
  const [uid, setUid] = useState("");
  const [ask, setAsk] = useState(null);
  const run = async () => {
    const res = await api(`/admin/founding/${ask.id}/${ask.action}`, { method: "POST" });
    toast.success(res.founding_crush ? "Founding Crush granted." : "Founding Crush revoked.");
    setUid("");
    reload();
  };
  const pct = data?.limit ? Math.min(100, Math.round((data.auto_assigned / data.limit) * 100)) : 0;

  return (
    <div className="space-y-6" data-testid="launch-founding-tab">
      <LoadState loading={loading} error={error} onRetry={reload}>
        {data && (<>
          <div className="grid gap-6 lg:grid-cols-[1.2fr_1fr]">
            <Panel className="p-6" data-testid="founding-counter">
              <div className="flex items-center gap-2"><Medal size={20} weight="duotone" className="cw-brand-text" aria-hidden /><p className="cw-soft text-sm">Founding Crush</p>{!data.flag_enabled && <Pill tone="warning" data-testid="founding-flag-off">Badge flag is off</Pill>}</div>
              <p className="mt-3 text-4xl font-semibold tabular-nums" data-testid="founding-count">{data.auto_assigned}<span className="cw-muted text-xl font-normal"> / {data.limit}</span></p>
              <div className="mt-3 h-2 overflow-hidden rounded-full bg-[var(--cw-surface-tertiary)]"><div className="h-full rounded-full bg-[var(--cw-brand)]" style={{ width: `${pct}%` }} /></div>
              <p className="cw-soft mt-2 text-sm">{data.remaining} left · {data.count} people hold the badge now (including manual grants). Test accounts don’t count.</p>
            </Panel>
            <Panel className="p-6">
              <form onSubmit={(e) => { e.preventDefault(); setAsk({ id: uid.trim(), action: "grant" }); }} data-testid="founding-grant-form">
                <h3 className="font-semibold">Grant by hand</h3>
                <Label htmlFor="fd-uid" className="mt-3 block">User ID</Label>
                <div className="mt-2 flex gap-2"><Input id="fd-uid" value={uid} onChange={(e) => setUid(e.target.value)} className="font-mono text-sm" data-testid="founding-user-input" />
                  <Button type="submit" disabled={uid.trim().length !== 24} data-testid="founding-grant-button">Grant</Button></div>
              </form>
            </Panel>
          </div>
          <Panel className="overflow-hidden">
            {data.items.length ? <ul className="divide-y divide-border" data-testid="founding-list">{data.items.map((x) => (
              <li key={x.user_id} className="flex items-center gap-4 px-5 py-3 text-sm" data-testid={`founding-row-${x.user_id}`}>
                <span className="w-12 tabular-nums font-semibold">{x.number ? `#${x.number}` : "—"}</span>
                <Link to={`/admin/users/${x.user_id}`} className="min-w-0 flex-1 truncate hover:underline">{x.first_name || x.user_id}</Link>
                <Pill className="capitalize">{x.source || "auto"}</Pill>
                <span className="cw-muted hidden w-40 text-xs sm:block">{formatDate(x.at)}</span>
                <Button size="sm" variant="outline" onClick={() => setAsk({ id: x.user_id, action: "revoke", name: x.first_name })} data-testid={`founding-revoke-button-${x.user_id}`}>Revoke</Button>
              </li>))}</ul> : <Empty title="No Founding Crushes yet" text="The first people to finish onboarding get it automatically." testId="founding-empty" />}
          </Panel>
        </>)}
      </LoadState>
      <ConfirmDialog open={!!ask} onOpenChange={(o) => !o && setAsk(null)} destructive={ask?.action === "revoke"} testId="founding-confirm-dialog" onConfirm={run}
        title={ask?.action === "grant" ? "Grant the Founding Crush badge?" : `Revoke ${ask?.name || "this person"}’s Founding Crush badge?`}
        confirmLabel={ask?.action === "grant" ? "Grant" : "Revoke"} description={ask?.action === "grant" ? `User ${ask?.id}. Manual grants don’t use up the automatic count.` : "The badge disappears from their profile."} />
    </div>
  );
}
